import { FaceSmileIcon } from '@heroicons/react/24/outline';
import { api } from 'aws-blocks';
import { useEffect, useMemo, useRef, useState } from 'react';
import EmoteIcon from '../../Components/EmoteIcon';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import {
  EMOTE_LOADOUT_SIZE,
  getEmoteDef,
  resolveEmoteLoadout,
} from '../../emotes/registry';
import useApiAction from '../../hooks/use-api-action';

export type EmoteWheelProps = {
  gameId: string;
  enabled?: boolean;
  onSent?: (game: Record<string, unknown>) => void;
};

const DISPLAY_MS = 3200;
const FAN_RADIUS = 92;
const FAN_START = (178 * Math.PI) / 180;
const FAN_END = (88 * Math.PI) / 180;

function fanOffset(index: number, count: number) {
  const t = count <= 1 ? 0.5 : index / (count - 1);
  const angle = FAN_START + (FAN_END - FAN_START) * t;
  return {
    x: Math.cos(angle) * FAN_RADIUS,
    y: Math.sin(angle) * FAN_RADIUS,
  };
}

export default function EmoteWheel({
  gameId,
  enabled = true,
  onSent,
}: EmoteWheelProps) {
  const { userData } = useAuthContext();
  const { addSnackBar } = useSnackBarContext();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, setTick] = useState(0);

  const loadout = useMemo(
    () =>
      resolveEmoteLoadout(
        userData?.emoteLoadout,
        userData?.ownedColorIds,
        userData?.ownedEmoteIds
      ).slice(0, EMOTE_LOADOUT_SIZE),
    [userData?.emoteLoadout, userData?.ownedColorIds, userData?.ownedEmoteIds]
  );

  const [send, sending] = useApiAction({
    action: async (payload: { emoteId?: string }) =>
      api.sendEmote(gameId, String(payload.emoteId)),
  });

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 250);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onCooldown = cooldownUntil > Date.now();
  const cooldownSec = onCooldown
    ? Math.max(1, Math.ceil((cooldownUntil - Date.now()) / 1000))
    : 0;

  async function handleSend(emoteId: string) {
    if (!enabled || onCooldown || sending) return;
    try {
      const game = (await send({ emoteId })) as Record<string, unknown>;
      setCooldownUntil(Date.now() + 8000);
      setOpen(false);
      onSent?.(game);
    } catch (err) {
      addSnackBar({
        title: 'Emote',
        message: err instanceof Error ? err.message : String(err),
        variant: 'warning',
        timeout: 2000,
      });
    }
  }

  if (!enabled) return null;

  return (
    <div ref={rootRef} className="relative">
      {open ? (
        <div className="pointer-events-none absolute left-1/2 top-1/2 z-50" role="menu">
          {loadout.map((id, index) => {
            const def = getEmoteDef(id);
            if (!def) return null;
            const { x, y } = fanOffset(index, loadout.length);
            return (
              <div
                key={id}
                className="bk-emote-fan-item pointer-events-auto absolute left-0 top-0 -ml-5 -mt-5 h-10 w-10"
                style={{
                  ['--fan-x' as string]: `${x}px`,
                  ['--fan-y' as string]: `${y}px`,
                  animationDelay: `${index * 28}ms`,
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  disabled={sending || onCooldown}
                  title={def.label}
                  onClick={() => void handleSend(id)}
                  className="flex h-full w-full items-center justify-center rounded-full border border-bk-gold-dim/70 bg-bk-panel/95 shadow-[0_0_16px_rgba(0,0,0,0.45)] backdrop-blur-sm transition hover:scale-110 hover:border-bk-gold disabled:opacity-40"
                >
                  <EmoteIcon emote={def} size="md" />
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
      <button
        type="button"
        disabled={sending}
        onClick={() => setOpen((v) => !v)}
        className={[
          'relative z-[51] flex h-8 w-8 items-center justify-center border text-[11px] font-bold tabular-nums transition-all',
          open
            ? 'border-bk-gold bg-bk-gold/20 text-bk-gold-bright shadow-[0_0_14px_color-mix(in_srgb,var(--color-bk-gold)_35%,transparent)]'
            : 'border-bk-gold-dim bg-bk-panel/90 text-bk-gold hover:border-bk-gold hover:text-bk-gold-bright',
          onCooldown ? 'opacity-70' : '',
        ].join(' ')}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={onCooldown ? `Emote cooldown ${cooldownSec}` : 'Send emote'}
      >
        {onCooldown ? (
          cooldownSec
        ) : (
          <FaceSmileIcon className="h-4 w-4" aria-hidden />
        )}
      </button>
    </div>
  );
}

export { DISPLAY_MS as EMOTE_DISPLAY_MS };
